import { IconButton } from '@atlaskit/button/new';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import ShowMoreVerticalIcon from '@atlaskit/icon/core/show-more-vertical';
import { Inline, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import {
  type DatabaseFilter,
  isFilterWithUsageType,
} from '@databases/types/database/view';
import { useFilterStringRepresentation } from '@databases/view-settings/hooks/useFilterStringRepresentation';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../i18n';

const containerStyles = xcss({
  borderColor: 'color.border',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderRadius: 'border.radius.100',
  paddingBlock: 'space.100',
  paddingInline: 'space.200',
});

export interface ViewSettingsFilterRowProps {
  contentId: string;
  filter: DatabaseFilter;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const ViewSettingsFilterRuleView = ({
  contentId,
  filter,
  onEdit,
  onDelete,
}: ViewSettingsFilterRowProps) => {
  const { formatMessage } = useIntl();
  const [areMoreActionsExpanded, setAreMoreActionsExpanded] = useState(false);

  const handleClickEdit = useCallback(() => {
    onEdit?.();
  }, [onEdit]);

  const handleClickMore = useCallback(() => {
    setAreMoreActionsExpanded(true);
  }, []);

  const fieldId = isFilterWithUsageType(filter) ? undefined : filter.fieldId;
  const filterStringRepresentation = useFilterStringRepresentation({ contentId, filter });

  return (
    <Inline alignBlock="center" grow="fill" spread="space-between" xcss={containerStyles}>
      <Tooltip
        content={filterStringRepresentation}
        // The content of the tooltip is equivalent to the content of the target, so screen readers
        // don't need a duplicate announcement. The tooltip is to help non-screen reader users in
        // case the target content is truncated by CSS due to length
        isScreenReaderAnnouncementDisabled
      >
        {(tooltipProps) => (
          <span {...tooltipProps}>
            <Text color={!fieldId ? 'color.text.subtlest' : undefined} maxLines={1}>
              {filterStringRepresentation}
            </Text>
          </span>
        )}
      </Tooltip>
      {areMoreActionsExpanded ? (
        <Inline alignBlock="center" grow="hug" space="space.100">
          {onEdit && (
            <IconButton
              appearance="subtle"
              icon={EditIcon}
              label={formatMessage(
                messages['view-settings-panel-filter.filter-edit-action-label']
              )}
              onClick={handleClickEdit}
            />
          )}
          {onDelete && (
            <IconButton
              appearance="subtle"
              icon={DeleteIcon}
              label={formatMessage(
                messages['view-settings-panel-filter.filter-delete-action-label']
              )}
              onClick={onDelete}
            />
          )}
        </Inline>
      ) : (
        <IconButton
          appearance="subtle"
          icon={ShowMoreVerticalIcon}
          isDisabled={!onEdit && !onDelete}
          label={formatMessage(
            messages['view-settings-panel-filter.filter-show-more-actions-label']
          )}
          onClick={handleClickMore}
        />
      )}
    </Inline>
  );
};
